import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../../../utils/api';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { toast } from 'sonner';
import { Loader2, Wrench, Package, CheckSquare, Square, Pencil, Save, X } from 'lucide-react';

const fmt = (n) => (n || 0).toLocaleString('sv-SE', { maximumFractionDigits: 0 });

// Verktyg & förbrukning sektion — visas inom projektets Material-flik efter en
// godkänd materialplan. Admin kan markera "finns i lager" per rad → sparas i Lager.
const ToolsPlanSection = ({ projectId, materialPlanId }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.listToolsPlans(projectId);
      setPlans(r.data?.plans || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  // Poll generating plans
  useEffect(() => {
    const gens = plans.filter(p => p.status === 'generating');
    if (!gens.length) return;
    const iv = setInterval(async () => {
      const upd = await Promise.all(gens.map(p => api.getToolsPlan(p.id).then(r => r.data).catch(() => null)));
      setPlans(prev => prev.map(p => upd.find(u => u?.id === p.id) || p));
    }, 4000);
    return () => clearInterval(iv);
  }, [plans]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await api.generateToolsPlan({ project_id: projectId, material_plan_id: materialPlanId || null });
      toast.success('Verktygsberäkning startad');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte starta beräkning');
    } finally { setGenerating(false); }
  };

  const toggleStock = async (planId, toolIdx, inStock, brand, model) => {
    try {
      const r = await api.toggleToolInStock(planId, { tool_index: toolIdx, in_stock: inStock, brand_override: brand, model_override: model });
      setPlans(prev => prev.map(p => {
        if (p.id !== planId) return p;
        const tools = [...(p.tools || [])];
        tools[toolIdx] = r.data?.tool || tools[toolIdx];
        return { ...p, tools };
      }));
      toast.success(inStock ? 'Tillagd i lager' : 'Borttagen från lager');
    } catch {
      toast.error('Kunde inte uppdatera');
    }
  };

  return (
    <div className="mt-6 border-t-2 border-dashed border-border pt-6" data-testid="tools-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-semibold flex items-center gap-2"><Wrench size={16} /> Verktyg & Förbrukning (firmans resurser)</h3>
          <p className="text-xs text-muted-foreground">AI listar verktyg + förbrukning för utförandet. Markera "finns i lager" så sparas de i Resurser → Lager.</p>
        </div>
        <Button
          size="sm"
          onClick={handleGenerate}
          disabled={generating}
          variant="outline"
          data-testid="btn-generate-tools"
        >
          {generating ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Wrench size={14} className="mr-1" />}
          Beräkna verktyg
        </Button>
      </div>

      {loading && <Loader2 className="animate-spin text-muted-foreground mx-auto my-4" />}

      {!loading && plans.length === 0 && (
        <p className="text-xs text-muted-foreground italic">Inga verktygsberäkningar än.</p>
      )}

      {plans.map(p => <ToolsCard key={p.id} plan={p} onToggle={toggleStock} />)}
    </div>
  );
};

const ToolsCard = ({ plan, onToggle }) => {
  const [expanded, setExpanded] = useState(true);
  const [editIdx, setEditIdx] = useState(-1);
  const [editBrand, setEditBrand] = useState('');
  const [editModel, setEditModel] = useState('');

  const isGenerating = plan.status === 'generating';
  const isFailed = plan.status === 'failed';
  const tools = plan.tools || [];
  const inStockCount = tools.filter(t => t.in_stock).length;

  const startEdit = (i, t) => {
    setEditIdx(i);
    setEditBrand(t.brand_suggestion || '');
    setEditModel(t.model_suggestion || '');
  };

  const saveEdit = (i, t) => {
    onToggle(plan.id, i, true, editBrand, editModel);
    setEditIdx(-1);
  };

  return (
    <Card className="mb-3" data-testid={`tools-card-${plan.id}`}>
      <button onClick={() => setExpanded(e => !e)} className="w-full p-3 text-left hover:bg-muted/30 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm font-medium">
            {new Date(plan.created_at).toLocaleDateString('sv-SE')} · {tools.length} verktyg
            {isGenerating && <span className="text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded flex items-center gap-1"><Loader2 size={9} className="animate-spin" /> Genererar</span>}
            {isFailed && <span className="text-xs bg-red-100 text-red-800 px-1.5 py-0.5 rounded">Fel</span>}
          </div>
          {!isGenerating && !isFailed && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {inStockCount}/{tools.length} i lager · Totalt estimat: {fmt(plan.total_estimated_cost)} kr
            </p>
          )}
          {isFailed && <p className="text-xs text-red-600 mt-0.5">{plan.error_message}</p>}
        </div>
      </button>
      {expanded && !isGenerating && !isFailed && (
        <div className="border-t border-border px-3 py-2 space-y-1 bg-background">
          {tools.map((t, i) => (
            <div key={i} className="flex items-center gap-2 py-1.5 border-b border-dashed border-border/40 last:border-0 text-xs" data-testid={`tool-row-${i}`}>
              <button
                onClick={() => onToggle(plan.id, i, !t.in_stock, t.brand_suggestion, t.model_suggestion)}
                className={`flex-shrink-0 ${t.in_stock ? 'text-emerald-600' : 'text-muted-foreground hover:text-foreground'}`}
                title={t.in_stock ? 'I lager — klicka för att ta bort' : 'Markera som i lager'}
                data-testid={`stock-toggle-${i}`}
              >
                {t.in_stock ? <CheckSquare size={16} /> : <Square size={16} />}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1 flex-wrap">
                  <span className="font-medium">{t.name}</span>
                  {t.critical && <span className="text-[10px] text-red-600 font-bold">● KRITISK</span>}
                  <span className="text-muted-foreground">·</span>
                  {editIdx === i ? (
                    <span className="flex items-center gap-1">
                      <Input value={editBrand} onChange={(e) => setEditBrand(e.target.value)} placeholder="Märke" className="h-6 w-20 text-xs" />
                      <Input value={editModel} onChange={(e) => setEditModel(e.target.value)} placeholder="Modell" className="h-6 w-28 text-xs" />
                      <button onClick={() => saveEdit(i, t)} className="text-emerald-600"><Save size={12} /></button>
                      <button onClick={() => setEditIdx(-1)} className="text-muted-foreground"><X size={12} /></button>
                    </span>
                  ) : (
                    <>
                      <span className="text-muted-foreground">{t.brand_suggestion} {t.model_suggestion}</span>
                      {t.in_stock && (
                        <button onClick={() => startEdit(i, t)} className="text-muted-foreground hover:text-foreground ml-1" title="Byt märke/modell"><Pencil size={10} /></button>
                      )}
                    </>
                  )}
                </div>
                {t.where_to_buy && <p className="text-[10px] text-muted-foreground mt-0.5">{t.where_to_buy}</p>}
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-[10px] text-muted-foreground">{t.quantity} {t.unit} à {fmt(t.unit_price_sek)}</div>
                <div className={`text-xs font-bold ${t.in_stock ? 'text-emerald-600 line-through' : ''}`}>{fmt(t.total_sek)} kr</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default ToolsPlanSection;
